import type { Option } from "../core/option.ts";
import { elementAt } from "../core/checked-index.ts";
import { type SyncKey } from "../ids/ids.ts";
import type { RowSyncState } from "./sync-state.ts";

/**
 * A row's `RowSyncState` read as its sync stages, for a surface that renders
 * staged sync progress (e.g. `weather_stage1`, `weather_stage2`):
 * `RowSyncState.stagesOf` answers them, and `RowSyncState.fromKeys` records
 * each key's stage once instead of re-parsing it from the sync key on
 * every render.
 */
export namespace StageLabels {
  /** Stage label of one sync key: its parsed `stage`, else the key suffix. */
  export function stageOf(syncKey: SyncKey, parsed: Option<string>): Option<string> {
    if (parsed != null) return parsed;
    const match = syncKey.match(/[@_]([a-zA-Z]\w*)$/);
    return match == null ? null : elementAt(match, 1, "sync-stages: stage suffix");
  }

  /**
   * Distinct stage labels across the state's sync keys, in stage order:
   * numbered `_stageN` suffixes sort by N, other labels keep key order.
   */
  export function collectStages(state: RowSyncState): readonly string[] {
    const labels: string[] = [];
    const seen = new Set<string>();
    for (const [syncKey, status] of state.syncKeys) {
      const label = stageOf(syncKey, status.stage);
      if (label == null || seen.has(label)) continue;
      seen.add(label);
      labels.push(label);
    }
    const order = (label: string): number => {
      const match = label.match(/^stage(\d+)$/);
      return match == null ? Number.POSITIVE_INFINITY : Number(elementAt(match, 1, "sync-stages: stage number"));
    };
    return labels
      .map((label, index) => ({ label, index }))
      .sort((a, b) => order(a.label) - order(b.label) || a.index - b.index)
      .map((entry) => entry.label);
  }
}
